import { ChangeEvent, JSX, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import FeedbackButton from "lib/components/button/FeedbackButton";
import ComboBox from "lib/ui/textfield/ComboBox";

import { getLoggedInUser } from "authentication/AuthenticationService";
import {
	BlueprintInfo,
	BlueprintType,
	getBlueprintsByType
} from "core/blueprint/base/BlueprintService";
import {
	Role,
	UserRights,
	getTrajectoryBlueprintRoles,
	getTrajectoryBlueprintUsers
} from "core/blueprint/trajectory/TrajectoryBlueprintService";

import { asTitle } from "utils/Utils";

import FileUpload from "lib/components/form/FileUpload";
import { canUserModifyRights } from "trajectory/AccessUtil";
import {
	InformationDto,
	createTrajectory,
	updateTrajectoryInformation
} from "trajectory/TrajectoryService";
import Access from "trajectory/create/Access";

interface DialogProps {
	onClose(): void;
	onConfirm(templateId: number): void;
}

export default function CreateTrajectoryDialog(
	props: Readonly<DialogProps>
): JSX.Element {
	const { t } = useTranslation();

	const [templates, setTemplates] = useState<BlueprintInfo[]>([]);
	const [loadingFailed, setLoadingFailed] = useState(false);

	const [selected, setSelected] = useState<number>(0);
	const [name, setName] = useState("");
	const [description, setDescription] = useState("");
	const [roles, setRoles] = useState<Role[]>([]);
	const [users, setUsers] = useState<UserRights[]>([]);
	const [patches, setPatches] = useState<InformationDto>({});

	const [nameError, setNameError] = useState(false);
	const [templateError, setTemplateError] = useState(false);
	const [accessError, setAccessError] = useState(false);

	useEffect(() => {
		getBlueprintsByType(BlueprintType.TRAJECTORY)
			.then(data => data.sort((a, b) => a.name.localeCompare(b.name)))
			.then(data => setTemplates(data))
			.catch(() => setLoadingFailed(true));
	}, []);

	function isValidName(value: string = name): boolean {
		return value.trim().length > 0;
	}

	const updateName = useCallback(
		(event: ChangeEvent<HTMLInputElement>): void => {
			const value = event.target.value;
			setName(value);
			setNameError(!isValidName(value));
		},
		[]
	);

	const onChangeDescription = useCallback(
		(event: ChangeEvent<HTMLInputElement>): void => {
			setDescription(event.target.value);
		},
		[]
	);

	function isValidTemplate(value: number = selected): boolean {
		return templates.findIndex(t => t.id === value) !== -1;
	}

	const onSelectBlueprint = useCallback(
		(blueprint: BlueprintInfo | null): void => {
			setTemplateError(blueprint === null);
			if (blueprint === null) {
				return;
			}

			setSelected(blueprint.id);
			if (name.trim() === "" && blueprint?.name) {
				setName(blueprint.name);
			}

			// TODO: handle error.
			getTrajectoryBlueprintRoles(blueprint.id).then(setRoles);
			getTrajectoryBlueprintUsers(blueprint.id).then(setUsers);
		},
		[name]
	);

	function checkValidTemplate(): boolean {
		const valid = isValidTemplate();
		setTemplateError(!valid);
		return valid;
	}

	function checkValidName(): boolean {
		const valid = isValidName();
		setNameError(!valid);
		return valid;
	}

	function checkValidAdminUser(): boolean {
		const valid = canUserModifyRights(getLoggedInUser(), roles, users);
		setAccessError(!valid);
		return valid;
	}

	function checkValid(): boolean {
		let valid = checkValidName();
		valid &&= checkValidTemplate();
		valid &&= checkValidAdminUser();
		return valid;
	}

	function createDocument(): Promise<void> {
		if (!checkValid()) {
			return Promise.resolve();
		}

		const trajectory = {
			blueprintId: selected,
			name,
			description,
			access: {
				roles,
				users
			}
		};

		return createTrajectory(trajectory)
			.then(location =>
				Number.parseInt(
					location.substring(location.lastIndexOf("/") + 1)
				)
			)
			.then(async id => {
				if (Object.keys(patches).length > 0) {
					await updateTrajectoryInformation(id, patches).catch(() => {
						console.error(
							"Failed to update trajectory information"
						);
					});
				}
				props.onConfirm(id);
			});
	}

	const uploadTrajectoryInformation = useCallback(
		(data: string[][]): void => {
			if (data.length < 2) {
				// No valid data.
				return;
			}

			const patches: InformationDto = {};
			const columns = data[0].length;
			for (let column = 0; column < columns; ++column) {
				const fieldName = data[0][column];
				const value = data[1][column];

				patches[fieldName] = value;
			}

			setPatches(patches);
		},
		[]
	);

	return (
		<Dialog
			open
			fullWidth
			maxWidth="md"
			scroll="paper"
			PaperProps={{ sx: { height: "90vh" } }}
			onClose={props.onClose}>
			<DialogTitle>
				{asTitle(t("newDocument", { document: "trajectory" }))}
			</DialogTitle>
			<DialogContent>
				{loadingFailed && (
					<Alert severity="error">
						{t("loadDocumentError", { document: "template_other" })}
					</Alert>
				)}
				<Grid container spacing={2} direction="column">
					<Grid item>
						<ComboBox
							label={t("template")}
							required
							options={templates}
							error={templateError}
							getOptionLabel={template => template.name}
							getOptionKey={template => template.id}
							onChange={onSelectBlueprint}
						/>
					</Grid>
					<Grid item>
						<TextField
							label={asTitle(t("title"))}
							fullWidth
							required
							inputProps={{ maxLength: 255 }}
							helperText={name.length.toString() + "/255"}
							error={nameError}
							value={name}
							onChange={updateName}
							variant="standard"
						/>
					</Grid>
					<Grid item>
						<TextField
							label={asTitle(t("description"))}
							fullWidth
							multiline
							minRows={3}
							inputProps={{ maxLength: 1000 }}
							helperText={description.length.toString() + "/1000"}
							value={description}
							onChange={onChangeDescription}
							variant="standard"
						/>
					</Grid>

					<Grid item>
						<Access
							users={users}
							roles={roles}
							onChangeRoles={setRoles}
							onChangeUsers={setUsers}
						/>
						{accessError && (
							<Typography
								variant="caption"
								sx={{ color: "error.main" }}>
								{t("principalRequiredRights")}
							</Typography>
						)}
					</Grid>

					<Grid item xs={12}>
						<FileUpload
							label={t("dataFile")}
							onLoadData={uploadTrajectoryInformation}
						/>
					</Grid>
				</Grid>
			</DialogContent>
			<DialogActions>
				<Button variant="outlined" onClick={props.onClose}>
					{t("cancel")}
				</Button>
				<FeedbackButton
					variant="contained"
					label={t("confirm")}
					onClick={createDocument}
				/>
			</DialogActions>
		</Dialog>
	);
}
